import {
  AnswerCreateDTO,
  FillInTheBlankOptionCreateDTO,
  QuestionCreateDTO,
  OrderingItemCreateDTO,
  PositionalAnswerCreateDTO,
  QuizAnswerCreateDTO,
} from '@/models/Answer';
import { findMaximumBipartiteMatching } from '@/utils/graphs/maximumBipartiteMatching';
import { formatList } from '@/utils/i18n/formatList';
import { TFunction } from 'i18next';

const hasEmptyAnswerContent = (answers: AnswerCreateDTO[]) =>
  answers.some(({ text, mediaFile }) => !text?.trim() && !mediaFile);

export const findUncoveredPositions = (
  items: { validPositions: number[] }[],
  positionCount: number,
): number[] => {
  const covered = new Set(
    items.flatMap(({ validPositions }) => validPositions),
  );
  const uncovered: number[] = [];
  for (let position = 1; position <= positionCount; position += 1) {
    if (!covered.has(position)) uncovered.push(position);
  }
  return uncovered;
};

export const hasPerfectPositionMatching = (
  items: { validPositions: number[] }[],
  positionCount: number,
) => {
  const adjacency = items.map(({ validPositions }) =>
    validPositions.map(position => position - 1),
  );
  const matching = findMaximumBipartiteMatching(adjacency, positionCount);
  return matching.size === positionCount;
};

export const validateQuizAnswers = (
  answers: QuizAnswerCreateDTO[],
  t: TFunction,
) => {
  if (answers.length === 0) return t('minOptionsRequired', { count: 1 });
  if (hasEmptyAnswerContent(answers)) return t('optionContentRequired');
  if (!answers.some(({ isCorrect }) => isCorrect)) {
    return t('atLeastOneCorrectOption');
  }
  return true;
};

export const validateOrderingAnswers = (
  items: OrderingItemCreateDTO[],
  t: TFunction,
) => {
  if (items.length < 2) return t('minOptionsRequired', { count: 2 });
  if (hasEmptyAnswerContent(items)) return t('optionContentRequired');
  if (!items.some(({ validPositions }) => validPositions.length > 0)) {
    return t('atLeastOneCorrectOption');
  }
  return true;
};

export const validateCombinationQuestions = (
  items: QuestionCreateDTO[],
  t: TFunction,
) => {
  if (items.length < 2) return t('minOptionsRequired', { count: 2 });
  if (hasEmptyAnswerContent(items)) return t('optionContentRequired');
  return true;
};

export const validateCombinationAnswers = (
  answers: PositionalAnswerCreateDTO[],
  questions: QuestionCreateDTO[],
  t: TFunction,
  tCombination: TFunction,
) => {
  if (answers.length < 2) return t('minOptionsRequired', { count: 2 });
  if (hasEmptyAnswerContent(answers)) return t('optionContentRequired');
  if (!answers.some(({ validPositions }) => validPositions.length > 0)) {
    return t('atLeastOneCorrectOption');
  }

  const uncoveredItems = findUncoveredPositions(answers, questions.length);
  if (uncoveredItems.length) {
    return tCombination('everyQuestionNeedsAnswer', {
      count: uncoveredItems.length,
      items: formatList(uncoveredItems),
    });
  }

  if (!hasPerfectPositionMatching(answers, questions.length)) {
    return tCombination('notEnoughDistinctAnswers');
  }

  return true;
};

export const validateFillInTheBlankOptions = (
  options: FillInTheBlankOptionCreateDTO[],
  blankCount: number,
  t: TFunction,
  tFillInTheBlank: TFunction,
) => {
  if (!options.length) return t('minOptionsRequired', { count: 1 });
  if (hasEmptyAnswerContent(options)) return t('optionContentRequired');

  const uncoveredBlanks = findUncoveredPositions(options, blankCount);
  if (uncoveredBlanks.length) {
    return tFillInTheBlank('everyBlankNeedsAnswer', {
      count: uncoveredBlanks.length,
      blanks: formatList(uncoveredBlanks),
    });
  }

  if (!hasPerfectPositionMatching(options, blankCount)) {
    return tFillInTheBlank('notEnoughDistinctOptions');
  }

  return true;
};
